import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Monitor, Tablet, Smartphone, RefreshCw, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { toast } from '@/components/ui/sonner';
import { useBrief, useUpdateBriefSlice, useSaveAndRebuildBrief } from '@/lib/brief';
import { useSession } from '@/lib/auth';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

/**
 * `/wyglad` — GUI builder for the public clinic site.
 *
 * Split-pane: control panel on the left, live preview iframe on the right.
 * Every form change re-renders the iframe by updating its URL query params
 * (debounced ~300ms). The preview hits the api-worker `/api/admin/wyglad/
 * preview` endpoint which server-renders the wireframe home page with the
 * in-progress design overlay — what the cloud rebuild WOULD produce if
 * the user saved right now.
 *
 * Saving is still a deliberate action via the SaveBar at the bottom of
 * the left pane: "Zapisz" persists `brief.design`, "Zapisz + zbuduj
 * podgląd" also enqueues a cloud rebuild so the public site updates.
 */

export const Route = createFileRoute('/_app/wyglad')({
  staticData: { crumb: 'Wygląd strony' },
  component: WygladPage,
});

type SkinId =
  | 'trust-clinical-a' | 'trust-clinical-b' | 'trust-clinical-c'
  | 'boutique-derm' | 'editorial-clinical'
  | 'vet-warm' | 'pediatric-family'
  | 'dental-modern' | 'aesthetic-luxury'
  | 'physio-active' | 'gp-friendly' | 'emergency-bold';
type HomeArchetype = 'standard' | 'boutique' | 'editorial';
type Tone = 'default' | 'surface' | 'dark' | 'accent';
type Density = 'compact' | 'comfortable' | 'spacious';
type HomeSection = 'proof' | 'team' | 'reviews' | 'about' | 'cta-final' | 'sticky-mobile-cta';
type Device = 'desktop' | 'tablet' | 'mobile';

interface DesignState {
  skin?: SkinId;
  home_archetype?: HomeArchetype;
  tone?: Tone;
  density?: Density;
  pages?: { home?: { disabled_sections?: HomeSection[] } };
}

interface SkinPreview {
  id: SkinId; name: string; description: string;
  bg: string; surface: string; text: string; accent: string; accent2?: string;
  fits: string[];
}

const SKINS: SkinPreview[] = [
  { id: 'trust-clinical-a', name: 'Trust Clinical A', description: 'Spokojny niebieski. Klinika rodzinna, GP, fizjoterapia.', bg: '#ffffff', surface: '#f4f6fb', text: '#0d1320', accent: '#1f5eea', accent2: '#0d9a8b', fits: ['gp','dental','physio','ortho','gyn'] },
  { id: 'trust-clinical-b', name: 'Trust Clinical B', description: 'Stonowany teal + miedź. Diagnostyka, ortopedia, USG.', bg: '#fbfbf8', surface: '#eef3f1', text: '#1a221d', accent: '#185c5c', accent2: '#a05c20', fits: ['ortho','physio','gp','dental'] },
  { id: 'trust-clinical-c', name: 'Trust Clinical C', description: 'Indygo + turkus. Diagnostyka, kardiologia, USG-3D.', bg: '#ffffff', surface: '#f4f5fb', text: '#0c0e1c', accent: '#5246d8', accent2: '#22c5b2', fits: ['ortho','gp','gyn','physio'] },
  { id: 'boutique-derm', name: 'Boutique Derm', description: 'Piasek + miedź, krój szeryfowy. Dermatologia, premium stomatologia.', bg: '#faf6f0', surface: '#f1eadc', text: '#231a13', accent: '#97631a', accent2: '#5a3b15', fits: ['derm','aesthetic','dental'] },
  { id: 'editorial-clinical', name: 'Editorial Clinical', description: 'Czerń + mocny akcent. Chirurgia plastyczna, premium gabinety.', bg: '#ffffff', surface: '#f4f4f4', text: '#0a0a0a', accent: '#d6481b', fits: ['aesthetic','derm','dental'] },
  { id: 'vet-warm', name: 'Vet Warm', description: 'Szałwia + krem. Małe zwierzęta, lecznice 24h.', bg: '#fbf9f4', surface: '#f0eee5', text: '#1c2218', accent: '#46743c', accent2: '#c47a40', fits: ['vet'] },
  { id: 'pediatric-family', name: 'Pediatric Family', description: 'Mięta + brzoskwinia. Pediatria, dentystyka dziecięca.', bg: '#fefaf6', surface: '#f7f1ed', text: '#1f1a22', accent: '#d7546a', accent2: '#5cb39a', fits: ['pediatric','dental','gp'] },
  { id: 'dental-modern', name: 'Dental Modern', description: 'Niebieski + turkus. Nowoczesna stomatologia, ortodoncja.', bg: '#fbfcfd', surface: '#f1f5f9', text: '#0e1a26', accent: '#00a39c', accent2: '#3563e8', fits: ['dental'] },
  { id: 'aesthetic-luxury', name: 'Aesthetic Luxury', description: 'Czerń + złoto, szeryfowy. Premium estetyczna, chirurgia plastyczna.', bg: '#0c0c0a', surface: '#1a1814', text: '#f6f1e6', accent: '#c9a165', fits: ['aesthetic','derm'] },
  { id: 'physio-active', name: 'Physio Active', description: 'Pomarańczowy + petrol. Fizjoterapia, rehabilitacja sportowa.', bg: '#ffffff', surface: '#f3f5f7', text: '#0e1517', accent: '#ff5a36', accent2: '#0a6f78', fits: ['physio','ortho'] },
  { id: 'gp-friendly', name: 'GP Friendly', description: 'Ciepła ochra + zieleń. Lekarz rodzinny, internista.', bg: '#fdfaf3', surface: '#f4ede0', text: '#221d12', accent: '#d76b27', accent2: '#477443', fits: ['gp','gyn','pediatric'] },
  { id: 'emergency-bold', name: 'Emergency Bold', description: 'Czerń + czerwień + żółć. Dyżury 24/7, izba przyjęć.', bg: '#0a0c10', surface: '#1c2027', text: '#f7fafc', accent: '#ff3b3b', accent2: '#ffd23f', fits: ['emergency','vet'] },
];

const HOME_ARCHETYPES: { id: HomeArchetype; name: string; description: string }[] = [
  { id: 'standard', name: 'Standard', description: 'Klasyka: hero · trust · usługi · zespół · opinie · o nas · kontakt · CTA.' },
  { id: 'boutique', name: 'Boutique', description: 'Cichy, premium: cinematic hero · filozofia · zabiegi · zespół.' },
  { id: 'editorial', name: 'Editorial', description: 'Magazynowo: cytat-hero · story · numbers · galeria.' },
];

const TONES: { id: Tone; label: string; description: string }[] = [
  { id: 'default', label: 'Domyślny', description: 'Tło z palety skórki.' },
  { id: 'surface', label: 'Stonowany', description: 'Strona na powierzchni surface.' },
  { id: 'dark', label: 'Ciemny', description: 'Inwersja kolorów — premium / 24h.' },
  { id: 'accent', label: 'Akcent', description: 'Tło w kolorze akcentu — odważne.' },
];

const DENSITIES: { id: Density; label: string; description: string }[] = [
  { id: 'compact', label: 'Gęsto', description: 'Mniej powietrza.' },
  { id: 'comfortable', label: 'Wygodnie', description: 'Domyślne odstępy.' },
  { id: 'spacious', label: 'Przestrzennie', description: 'Premium feel.' },
];

const HOME_SECTIONS: { id: HomeSection; label: string; description: string }[] = [
  { id: 'proof', label: 'Pasek zaufania', description: 'ocena · lata · pacjenci · lekarze' },
  { id: 'team', label: 'Zespół', description: 'Auto-ukrywa się jeśli <2 zdjęć' },
  { id: 'reviews', label: 'Opinie', description: '6 najlepszych + agregat' },
  { id: 'about', label: 'O klinice', description: 'Story + zdjęcie wnętrza' },
  { id: 'cta-final', label: 'CTA końcowe', description: 'Duża banera na końcu' },
  { id: 'sticky-mobile-cta', label: 'Pływające CTA (mobile)', description: 'Pasek „Umów · Zadzwoń"' },
];

const DEVICE_WIDTHS: Record<Device, number> = {
  desktop: 1280,
  tablet: 768,
  mobile: 390,
};

function WygladPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const saveAndRebuild = useSaveAndRebuildBrief();
  const { data: session } = useSession();
  const tenantSlug = session?.tenant?.slug;

  const initial: DesignState = React.useMemo(() => {
    const d = (brief as unknown as { design?: DesignState })?.design ?? {};
    return d;
  }, [brief]);

  const [skin, setSkin] = React.useState<SkinId | undefined>(initial.skin);
  const [archetype, setArchetype] = React.useState<HomeArchetype>(initial.home_archetype ?? 'standard');
  const [tone, setTone] = React.useState<Tone>(initial.tone ?? 'default');
  const [density, setDensity] = React.useState<Density>(initial.density ?? 'comfortable');
  const [disabled, setDisabled] = React.useState<Set<HomeSection>>(
    () => new Set(initial.pages?.home?.disabled_sections ?? [])
  );
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (dirty) return;
    setSkin(initial.skin);
    setArchetype(initial.home_archetype ?? 'standard');
    setTone(initial.tone ?? 'default');
    setDensity(initial.density ?? 'comfortable');
    setDisabled(new Set(initial.pages?.home?.disabled_sections ?? []));
  }, [initial, dirty]);

  const markDirty = () => setDirty(true);
  const toggleSection = (id: HomeSection) => {
    setDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    markDirty();
  };

  const buildPayload = (): DesignState => ({
    skin,
    home_archetype: archetype,
    tone,
    density,
    pages: { home: { disabled_sections: Array.from(disabled) } },
  });

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({ slice: 'design', data: buildPayload() });
      setSavedStatus('saved');
      setDirty(false);
      toast.success('Wygląd zapisany');
    } catch {
      setSavedStatus('error');
      toast.error('Nie udało się zapisać');
    }
  };

  const onSaveAndRebuild = async () => {
    setSavedStatus('idle');
    try {
      await saveAndRebuild.mutateAsync({ slice: 'design', data: buildPayload() });
      setSavedStatus('saved');
      setDirty(false);
      toast.success('Zapisano i zlecono nowy podgląd. Buduje się ~2-5 min.');
    } catch {
      setSavedStatus('error');
      toast.error('Nie udało się uruchomić podglądu');
    }
  };

  const subVertical = (() => {
    const v = (brief as unknown as { sub_vertical?: string })?.sub_vertical;
    if (typeof v === 'string') return v;
    const vert = (brief as unknown as { vertical?: string })?.vertical;
    return vert === 'vet' ? 'vet' : undefined;
  })();

  /* ─── live preview iframe URL — debounced query-string updates ─── */
  const apiBase = env.apiBaseUrl || '';
  const tenantParam = session?.role === 'operator' && tenantSlug ? `&tenant=${tenantSlug}` : '';
  const liveQuery = React.useMemo(() => {
    const parts: string[] = [];
    if (skin) parts.push(`skin=${skin}`);
    if (archetype) parts.push(`archetype=${archetype}`);
    if (tone) parts.push(`tone=${tone}`);
    if (density) parts.push(`density=${density}`);
    if (disabled.size > 0) parts.push(`disabled=${Array.from(disabled).join(',')}`);
    return parts.join('&');
  }, [skin, archetype, tone, density, disabled]);

  // Debounce: only push the iframe URL ~300ms after the latest change. This
  // keeps the iframe from re-fetching on every keystroke when we add
  // text-input controls later, and lets rapid tile-clicks coalesce.
  const [debouncedQuery, setDebouncedQuery] = React.useState(liveQuery);
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(liveQuery), 250);
    return () => window.clearTimeout(t);
  }, [liveQuery]);

  // Cache-bust on every change so the iframe doesn't think it's the same URL.
  const previewUrl = `${apiBase}/api/admin/wyglad/preview?${debouncedQuery}${tenantParam}`;
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const [device, setDevice] = React.useState<Device>('desktop');
  const [reloadKey, setReloadKey] = React.useState(0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave();
      }}
      // Full-bleed: cancel the parent _app padding so we can fill the viewport.
      className="-m-6 md:-m-8 flex h-[calc(100vh-4rem)] flex-col"
    >
      {/* TOP STRIP */}
      <header className="flex items-center justify-between gap-4 border-b border-border bg-bg px-6 py-3">
        <div>
          <h1 className="font-display text-lg font-semibold text-text">Wygląd strony</h1>
          <p className="text-xs text-text-muted">
            Każda zmiana renderuje się na żywo w podglądzie. Zapisz, gdy jesteś gotów —
            {tenantSlug ? (
              <code className="ml-1 rounded bg-surface px-1 py-0.5 font-mono text-[10px]">
                {tenantSlug}.pozyskajpacjenta.pl
              </code>
            ) : (
              ' publiczna strona'
            )}{' '}
            zaktualizuje się po przebudowie.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <DeviceToggle device={device} onChange={setDevice} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setReloadKey((k) => k + 1)}
            title="Odśwież podgląd"
          >
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => window.open(previewUrl, '_blank', 'noopener')}
            title="Otwórz w nowej karcie"
          >
            <ExternalLink className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </header>

      {/* SPLIT */}
      <div className="flex min-h-0 flex-1">
        {/* LEFT: controls */}
        <div className="w-full max-w-md shrink-0 overflow-y-auto border-r border-border bg-bg p-6 scrollbar-thin">
          <Section title="1 · Skórka" subtitle={subVertical ? `Rekomendowane dla ${subVertical} z badge'em "dopasowane".` : 'Paleta kolorów + typografia.'}>
            <ul role="list" className="grid grid-cols-2 gap-2">
              {SKINS.map((s) => {
                const isActive = skin === s.id;
                const fits = subVertical ? s.fits.includes(subVertical) : false;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => { setSkin(s.id); markDirty(); }}
                      className={cn(
                        'group relative block w-full overflow-hidden rounded-md text-left ring-1 transition-all',
                        isActive ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg' : 'ring-black/5 hover:ring-accent/40'
                      )}
                      title={s.description}
                    >
                      <div className="relative h-16" style={{ background: s.bg }}>
                        <div className="absolute inset-x-0 bottom-0 h-6" style={{ background: s.surface }} />
                        <div className="absolute left-2 top-2 flex items-center gap-1">
                          <span className="inline-block size-4 rounded-full ring-1 ring-white/40" style={{ background: s.accent }} aria-hidden="true" />
                          {s.accent2 ? (
                            <span className="inline-block size-4 rounded-full ring-1 ring-white/40" style={{ background: s.accent2 }} aria-hidden="true" />
                          ) : null}
                        </div>
                        <div className="absolute bottom-1 left-2 font-display text-xs" style={{ color: s.text }}>
                          Aa
                        </div>
                        {isActive ? (
                          <span className="absolute right-1 top-1 inline-flex size-4 items-center justify-center rounded-full bg-accent text-text-inverted">
                            <Check className="size-2.5" aria-hidden="true" />
                          </span>
                        ) : null}
                        {fits && !isActive ? (
                          <span className="absolute bottom-1 right-1 rounded-full bg-success/90 px-1 py-px text-[8px] font-medium text-text-inverted">
                            dopas.
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate p-1.5 text-[10px] font-medium text-text">{s.name}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>

          <Section title="2 · Układ strony głównej" subtitle="Trzy warianty kompozycji — różnią się kolejnością sekcji.">
            <ul role="list" className="space-y-1.5">
              {HOME_ARCHETYPES.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => { setArchetype(a.id); markDirty(); }}
                    className={cn(
                      'block w-full rounded-md p-2.5 text-left ring-1 transition-all',
                      archetype === a.id ? 'bg-accent/5 ring-accent' : 'bg-surface ring-black/5 hover:ring-accent/40'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-text">{a.name}</p>
                      {archetype === a.id ? <Check className="size-3.5 text-accent" aria-hidden="true" /> : null}
                    </div>
                    <p className="mt-0.5 text-xs text-text-muted">{a.description}</p>
                  </button>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="3 · Atmosfera" subtitle="Globalny ton tła. Akcent nie zmienia się.">
            <div className="grid grid-cols-2 gap-1.5">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setTone(t.id); markDirty(); }}
                  title={t.description}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-xs font-medium ring-1 transition-all',
                    tone === t.id ? 'bg-accent/10 text-accent ring-accent/30' : 'bg-surface text-text-muted ring-border hover:ring-accent/40'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="4 · Gęstość" subtitle="Ile powietrza między sekcjami.">
            <div className="grid grid-cols-3 gap-1.5">
              {DENSITIES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => { setDensity(d.id); markDirty(); }}
                  title={d.description}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-xs font-medium ring-1 transition-all',
                    density === d.id ? 'bg-accent/10 text-accent ring-accent/30' : 'bg-surface text-text-muted ring-border hover:ring-accent/40'
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </Section>

          <Section
            title="5 · Sekcje strony głównej"
            subtitle={archetype === 'standard' ? 'Wyłącz sekcje opcjonalne.' : 'Toggle aktywny tylko dla układu Standard.'}
            disabled={archetype !== 'standard'}
          >
            <ul role="list" className="space-y-1.5">
              {HOME_SECTIONS.map((sec) => {
                const isOff = disabled.has(sec.id);
                return (
                  <li key={sec.id}>
                    <button
                      type="button"
                      disabled={archetype !== 'standard'}
                      onClick={() => toggleSection(sec.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left ring-1 transition-all disabled:cursor-not-allowed',
                        isOff ? 'bg-surface ring-border opacity-60' : 'bg-accent/5 ring-accent/30'
                      )}
                    >
                      <div>
                        <p className="text-xs font-medium text-text">{sec.label}</p>
                        <p className="text-[10px] text-text-muted">{sec.description}</p>
                      </div>
                      <span
                        className={cn(
                          'inline-flex h-5 w-9 items-center rounded-full p-0.5 transition-colors',
                          isOff ? 'bg-surface ring-1 ring-border' : 'bg-accent'
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block size-4 transform rounded-full bg-bg shadow transition-transform',
                            isOff ? 'translate-x-0' : 'translate-x-4'
                          )}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>

          <SaveBar
            saving={update.isPending}
            rebuilding={saveAndRebuild.isPending}
            dirty={dirty}
            status={savedStatus}
            onCancel={() => {
              setSkin(initial.skin);
              setArchetype(initial.home_archetype ?? 'standard');
              setTone(initial.tone ?? 'default');
              setDensity(initial.density ?? 'comfortable');
              setDisabled(new Set(initial.pages?.home?.disabled_sections ?? []));
              setDirty(false);
            }}
            onSaveAndRebuild={onSaveAndRebuild}
          />
        </div>

        {/* RIGHT: live preview */}
        <div className="relative flex-1 bg-surface p-4">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all">
            <div
              className="overflow-hidden rounded-md bg-bg shadow-xl ring-1 ring-black/10"
              style={{
                width: `${DEVICE_WIDTHS[device]}px`,
                maxWidth: 'calc(100vw - 32rem)',
                height: 'calc(100vh - 10rem)',
              }}
            >
              <iframe
                ref={iframeRef}
                key={reloadKey}
                src={previewUrl}
                title="Podgląd"
                className="size-full border-0"
                // Allow same-origin so we can scroll/inspect; preview is read-only.
                sandbox="allow-same-origin allow-scripts"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

function Section({
  title,
  subtitle,
  disabled,
  children,
}: {
  title: string;
  subtitle?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={cn('mb-5', disabled && 'opacity-60')}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{title}</h2>
      {subtitle ? <p className="mb-2 mt-0.5 text-[11px] text-text-muted">{subtitle}</p> : <div className="h-2" />}
      {children}
    </section>
  );
}

function DeviceToggle({ device, onChange }: { device: Device; onChange: (d: Device) => void }) {
  const items: { id: Device; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
    { id: 'desktop', icon: Monitor, label: 'Desktop' },
    { id: 'tablet', icon: Tablet, label: 'Tablet' },
    { id: 'mobile', icon: Smartphone, label: 'Mobile' },
  ];
  return (
    <div className="inline-flex items-center rounded-md bg-surface ring-1 ring-border">
      {items.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          title={label}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-md text-text-muted transition-colors',
            device === id ? 'bg-accent/10 text-accent' : 'hover:text-text'
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
